// src/components/SystemHubMonitor.js

import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useSocket } from './SocketContext'; 
import './SystemHubMonitor.css'; 
import { 
    FaDatabase, FaMemory, 
    FaMicrochip, FaBolt, FaGlobeAmericas, FaTerminal, FaHeartbeat 
} from 'react-icons/fa';

export default function SystemHubMonitor() {
    const socket = useSocket();
    const [loading, setLoading] = useState(true);
    
    // Estados de Métricas
    const [serverStats, setServerStats] = useState({
        cpu: 12.5, // Iniciando como número float
        memory: 45.0,
        uptime: '14d 03h 22m',
        status: 'online'
    });

    // Começa com 1 pois você está logado
    const [activeSessions, setActiveSessions] = useState(1);
    const [dbStatus, setDbStatus] = useState('connected');
    const [latency, setLatency] = useState(24); 
    
    // Logs do "Terminal"
    const [systemLogs, setSystemLogs] = useState([
        { time: new Date().toLocaleTimeString(), type: 'info', msg: 'Monitoramento iniciado...' },
        { time: new Date().toLocaleTimeString(), type: 'success', msg: 'Conexão segura estabelecida.' },
        { time: new Date().toLocaleTimeString(), type: 'info', msg: 'Aguardando fluxo de dados...' },
    ]);

    const terminalEndRef = useRef(null);

    // Efeito de "Heartbeat" e atualização de dados simulados (CPU/RAM)
    useEffect(() => {
        setTimeout(() => setLoading(false), 800);

        const interval = setInterval(() => {
            setServerStats(prev => {
                // CORREÇÃO DO NaN: 
                // Convertemos para Float antes de calcular para garantir que é número
                const currentCpu = parseFloat(prev.cpu);
                const currentMem = parseFloat(prev.memory);

                // Oscilação suave
                const newCpu = Math.min(100, Math.max(5, currentCpu + (Math.random() * 6 - 3)));
                const newMem = Math.min(100, Math.max(20, currentMem + (Math.random() * 4 - 2)));

                return {
                    ...prev,
                    cpu: newCpu.toFixed(1), // Retorna string formatada apenas no final
                    memory: newMem.toFixed(1),
                };
            });
            
            // Latência oscilando levemente
            setLatency(Math.floor(Math.random() * (35 - 15 + 1) + 15));
        }, 2000);

        return () => clearInterval(interval);
    }, []);

    // Listener do Socket REAL
    useEffect(() => {
        if (socket) {
            // Se o seu backend emitir 'system_stats', o número atualizará para o real.
            // Caso contrário, ficará estático em 1 (você).
            socket.on('system_stats', (data) => {
                if (data.activeUsers !== undefined) {
                    setActiveSessions(data.activeUsers);
                }
            });

            return () => {
                socket.off('system_stats');
            }
        }
    }, [socket]);

    // Auto-scroll do terminal
    useEffect(() => {
        terminalEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [systemLogs]);

    const addLog = (type, msg) => {
        setSystemLogs(prev => [...prev.slice(-19), { 
            time: new Date().toLocaleTimeString(), type, msg
        }]);
    };

    const handlePingCheck = async () => {
        addLog('warning', 'Iniciando teste de latência...');
        const start = Date.now();
        try {
            // Tenta bater numa rota leve para ver o tempo de resposta real
            await api.get('/api/public/system-status'); 
            const ms = Date.now() - start;
            setLatency(ms);
            addLog('success', `Resposta do servidor: ${ms}ms`);
            toast.success(`Latência atual: ${ms}ms`);
        } catch (error) {
            addLog('error', 'Falha no teste de latência.');
        }
    };

    if (loading) return <div className="sh-monitor-loading">Inicializando telemetria...</div>;

    return (
        <div className="sh-monitor-wrapper">
            <div className="sh-monitor-header">
                <h1><FaHeartbeat /> Monitoramento de Saúde do Sistema</h1>
                <div className="sh-monitor-status-badge">
                    <span className="pulse-dot"></span> Sistema Operacional
                </div>
            </div>

            {/* GRID DE KPI PRINCIPAL */}
            <div className="sh-monitor-grid-top">
                {/* Card 1: CPU */}
                <div className="sh-monitor-card kpi-card">
                    <div className="kpi-icon cpu"><FaMicrochip /></div>
                    <div className="kpi-info">
                        <h3>Uso de CPU</h3>
                        <div className="kpi-value">{serverStats.cpu}%</div>
                        <div className="sh-progress-bar">
                            <div className="sh-progress-fill cpu" style={{ width: `${serverStats.cpu}%` }}></div>
                        </div>
                    </div>
                </div>

                {/* Card 2: Memória */}
                <div className="sh-monitor-card kpi-card">
                    <div className="kpi-icon memory"><FaMemory /></div>
                    <div className="kpi-info">
                        <h3>Memória RAM</h3>
                        <div className="kpi-value">{serverStats.memory}%</div>
                        <div className="sh-progress-bar">
                            <div className="sh-progress-fill memory" style={{ width: `${serverStats.memory}%` }}></div>
                        </div>
                    </div>
                </div>

                {/* Card 3: Conexões (CORRIGIDO) */}
                <div className="sh-monitor-card kpi-card">
                    <div className="kpi-icon globe"><FaGlobeAmericas /></div>
                    <div className="kpi-info">
                        <h3>Sessões Ativas</h3>
                        <div className="kpi-value">{activeSessions}</div>
                        <small>Usuários logados em tempo real</small>
                    </div>
                </div>

                {/* Card 4: Banco de Dados */}
                <div className="sh-monitor-card kpi-card">
                    <div className={`kpi-icon db ${dbStatus}`}><FaDatabase /></div>
                    <div className="kpi-info">
                        <h3>Banco de Dados</h3>
                        <div className="kpi-value-text">{dbStatus === 'connected' ? 'Conectado' : 'Erro'}</div>
                        <small>Latência: {latency}ms</small>
                    </div>
                </div>
            </div>

            {/* SEÇÃO INFERIOR: TERMINAL E AÇÕES */}
            <div className="sh-monitor-grid-bottom">
                
                {/* Terminal de Logs */}
                <div className="sh-monitor-card terminal-card">
                    <div className="terminal-header">
                        <FaTerminal /> System Event Log (Live)
                    </div>
                    <div className="terminal-body">
                        {systemLogs.map((log, idx) => (
                            <div key={idx} className={`terminal-line ${log.type}`}>
                                <span className="log-time">[{log.time}]</span>
                                <span className="log-msg">{log.msg}</span>
                            </div>
                        ))}
                        <div ref={terminalEndRef} />
                    </div>
                </div>

                {/* Painel de Ações Rápidas de Infra */}
                <div className="sh-monitor-card actions-infra-card">
                    <h3><FaBolt /> Ações de Infraestrutura</h3>
                    <div className="infra-actions-grid">
                        <button className="sh-btn-infra" onClick={handlePingCheck}>
                            Testar Conectividade (Ping)
                        </button>
                        <button className="sh-btn-infra" onClick={() => addLog('info', 'Limpeza de cache solicitada...')}>
                            Limpar Cache de API
                        </button>
                        <button className="sh-btn-infra danger" onClick={() => {
                            if(window.confirm('Isso reiniciará os serviços de websocket. Confirma?')) 
                                addLog('warning', 'Reiniciando serviços de socket...');
                        }}>
                            Reiniciar Serviços Socket
                        </button>
                        
                        <div className="server-info-box">
                            <p><strong>Server Uptime:</strong> {serverStats.uptime}</p>
                            <p><strong>Node Version:</strong> v18.16.0</p>
                            <p><strong>Ambiente:</strong> Production</p>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}